import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { MapPin, TrendingUp, TrendingDown } from "lucide-react";
import api from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { CARD, Label } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const SITE_COLORS = ["#065F46", "#B08D57", "#0EA5E9", "#8B5CF6", "#9CA3AF"];

export function useSiteRevenue(year, month) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/revenue-by-site", { params: { year, month } }).then((r) => setD(r.data)).catch(() => setD(false)); }, [year, month]);
  return d;
}

export function SiteSelect({ data, value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="site-filter" className="h-9 w-44 rounded-full"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Tous les sites</SelectItem>
        {data.sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function SiteRevenueCard({ year, month }) {
  const d = useSiteRevenue(year, month);
  const [site, setSite] = useState("all");
  if (!d || !d.sites.length) return null;
  const rows = site === "all" ? d.sites : d.sites.filter((s) => s.site_id === site);
  return (
    <div className={`${CARD} p-6`} data-testid="site-revenue-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-gray-400"><MapPin className="h-4 w-4" /><Label>CA encaissé par site · {d.label}</Label></div>
        <SiteSelect data={d} value={site} onChange={setSite} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {rows.map((s, i) => {
          const up = s.delta_pct != null && s.delta_pct >= 0;
          return (
            <div key={s.site_id} className="rounded-2xl bg-gray-50 p-4" data-testid={`site-rev-${s.site_id}`}>
              <p className="text-sm font-semibold" style={{ color: SITE_COLORS[d.sites.indexOf(s) % SITE_COLORS.length] }}>{s.name}</p>
              <p className="mt-1 font-display text-3xl font-black">{fmtEUR(s.total)}</p>
              <div className="mt-2 h-2 rounded-full bg-gray-200"><div className="h-2 rounded-full" style={{ width: `${s.share}%`, background: SITE_COLORS[d.sites.indexOf(s) % SITE_COLORS.length] }} /></div>
              <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span>{s.share} % du CA</span>
                {s.delta_pct != null ? <span className={`flex items-center gap-0.5 font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>{up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{up ? "+" : ""}{s.delta_pct} % vs mois précédent</span>
                  : <span>Mois précédent : {fmtEUR(s.prev_total)}</span>}
              </p>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] text-gray-400">Total du mois : {fmtEUR(d.total)} · d'après les encaissements journaliers saisis. Les dépenses restent en vue globale.</p>
    </div>
  );
}

export function SiteAnalysis({ year, month }) {
  const d = useSiteRevenue(year, month);
  const [site, setSite] = useState("all");
  if (!d || !d.sites.length) return null;
  const shown = site === "all" ? d.site_names : d.sites.filter((s) => s.site_id === site).map((s) => s.name);
  const cols = d.sites.filter((s) => site === "all" || s.site_id === site);
  return (
    <div className={`${CARD} p-6 lg:col-span-2`} data-testid="site-analysis">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2"><Label>Comparaison des sites — 12 mois</Label><SiteSelect data={d} value={site} onChange={setSite} /></div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d.twelve_months} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F2F4" />
            <XAxis dataKey="short" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => fmtEUR(v)} labelFormatter={(l, p) => p?.[0]?.payload?.label || l} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {shown.map((n) => <Bar key={n} dataKey={n} fill={SITE_COLORS[d.site_names.indexOf(n) % SITE_COLORS.length]} radius={[6, 6, 0, 0]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Label className="mb-2 mt-6">CA par praticien et par site · {d.label}</Label>
      {d.by_practitioner.length === 0 ? <p className="text-sm text-gray-400">Aucun encaissement ce mois-ci.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm" data-testid="site-prat-table">
            <thead><tr className="border-b border-gray-100 text-left text-xs text-gray-400"><th className="py-2">Praticien</th>{cols.map((s) => <th key={s.site_id} className="py-2 text-right">{s.name}</th>)}{site === "all" && <th className="py-2 text-right">Total</th>}</tr></thead>
            <tbody>{d.by_practitioner.map((p) => (
              <tr key={p.practitioner} className="border-b border-gray-50"><td className="py-2 font-medium">{p.practitioner}{p.retro_total > 0 && <p className="text-[11px] font-normal text-[#065F46]" data-testid="prat-retro-recue">dont reversement de rétrocession : {p.retro_recue.map((x) => `${x.from} ${fmtEUR(x.amount)}`).join(", ")}</p>}</td>
                {cols.map((s) => <td key={s.site_id} className="py-2 text-right">{p.by_site[s.site_id] ? fmtEUR(p.by_site[s.site_id]) : "—"}</td>)}
                {site === "all" && <td className="py-2 text-right font-semibold">{fmtEUR(p.total)}</td>}</tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
